import { Link, Separator, Text, type TextColor, type TextSize } from '@vizit/atoms';
import type { HeadingVariant } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import Markdown, { type Options, type Components } from 'react-markdown';
import { markdownSpacingClasses } from '../markdown-text/styles';
import type { SpacingDensity } from '../markdown-text/types';

/**
 * A faint rule between top-level blocks: sibling paragraphs (Spark writes one paragraph per idea) and
 * top-level list items. Nested content inside an idea stays unbroken.
 */
const DIVIDER_CLASSES = [
  '[&>p+p]:border-t [&>p+p]:border-secondary [&>p+p]:pt-12',
  '[&>ol>li+li]:border-t [&>ol>li+li]:border-secondary [&>ol>li+li]:pt-6 [&>ol>li+li]:mt-6',
  '[&>ul>li+li]:border-t [&>ul>li+li]:border-secondary [&>ul>li+li]:pt-6 [&>ul>li+li]:mt-6',
].join(' ');

/**
 * Enhanced text options for ideas markdown
 */
interface IdeasMarkdownTextOptions {
  /**
   * Text color
   */
  color?: TextColor;
  /**
   * Text size
   */
  size?: TextSize;
  /**
   * Spacing density between elements
   */
  spacing?: SpacingDensity;
}

/**
 * Heading customization options
 */
interface IdeasMarkdownHeadingOptions {
  /**
   * Custom className for all headings
   */
  className?: string;
  /**
   * Custom variants for specific heading levels
   */
  variants?: {
    h1?: HeadingVariant;
    h2?: HeadingVariant;
    h3?: HeadingVariant;
    h4?: HeadingVariant;
    h5?: HeadingVariant;
    h6?: HeadingVariant;
  };
}

/**
 * List customization options
 */
interface IdeasMarkdownListOptions {
  /**
   * Remove list markers (bullets/numbers)
   */
  removeMarkers?: boolean;
  /**
   * Custom className for list containers
   */
  containerClassName?: string;
  /**
   * Custom className for list items
   */
  itemClassName?: string;
}

/**
 * Props for the IdeasMarkdown component
 */
interface IdeasMarkdownProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The markdown content to render
   */
  children: string;
  /**
   * Additional CSS classes to apply to the markdown container
   */
  className?: string;
  /**
   * The options to use for the markdown elements
   */
  options?: Options;
  /**
   * The options to use for the text elements
   */
  textOptions?: IdeasMarkdownTextOptions;
  /**
   * The options to use for heading elements
   */
  headingOptions?: IdeasMarkdownHeadingOptions;
  /**
   * The options to use for list elements
   */
  listOptions?: IdeasMarkdownListOptions;
  /**
   * Draw a faint divider between top-level blocks (paragraphs and list items), e.g. one per Spark idea
   */
  dividers?: boolean;
  /**
   * The data-flow-id to use for the markdown container
   */
  'data-flow-id'?: string;
  /**
   * The components to use for the markdown elements
   */
  componentsOverrides?: Partial<Components>;
}

/**
 * The IdeasMarkdown component is used to render markdown content with enhanced customization
 * for ideas and recommendations, particularly for the Spark feature.
 *
 * @param props - The props for the IdeasMarkdown component.
 * @example
 * <IdeasMarkdown
 *   listOptions={{ removeMarkers: true }}
 *   headingOptions={{ variants: { h4: HeadingVariant.H5 } }}
 * >
 *   #### Recommendation
 *   - First point
 *   - Second point
 * </IdeasMarkdown>
 */
function IdeasMarkdown(props: IdeasMarkdownProps) {
  const {
    children,
    className,
    options,
    textOptions = {},
    headingOptions = {},
    listOptions = {},
    dividers = false,
    'data-flow-id': dataFlowId,
    componentsOverrides,
    ...other
  } = props;

  const { color, size, spacing = 'Comfortable' } = textOptions;
  const { className: headingClassName } = headingOptions;
  const { removeMarkers = false, containerClassName = '', itemClassName = '' } = listOptions;

  const spacingClasses = markdownSpacingClasses[spacing];

  const components: Components = {
    h1: ({ children }) => (
      <Text elementType="h1" className={cx(spacingClasses.elements, headingClassName)}>
        {children}
      </Text>
    ),
    h2: ({ children }) => (
      <Text elementType="h2" className={cx(spacingClasses.elements, headingClassName)}>
        {children}
      </Text>
    ),
    h3: ({ children }) => (
      <Text elementType="h3" className={cx(spacingClasses.elements, headingClassName)}>
        {children}
      </Text>
    ),
    h4: ({ children }) => (
      <Text elementType="h4" className={cx(spacingClasses.elements, headingClassName)}>
        {children}
      </Text>
    ),
    h5: ({ children }) => (
      <Text elementType="h5" className={cx(spacingClasses.elements, headingClassName)}>
        {children}
      </Text>
    ),
    h6: ({ children }) => (
      <Text elementType="h6" className={cx(spacingClasses.elements, headingClassName)}>
        {children}
      </Text>
    ),
    p: ({ children }) => (
      <Text elementType="p" className={cx('whitespace-pre-wrap', spacingClasses.elements)} color={color} size={size}>
        {children}
      </Text>
    ),
    li: ({ children }) => (
      <Text
        elementType="li"
        className={cx(
          'list-item relative',
          removeMarkers ? 'list-none' : '',
          removeMarkers ? 'pl-0' : 'pl-4',
          'mt-2 last:mb-0',
          itemClassName,
        )}
        color={color}
        size={size}
      >
        {children}
      </Text>
    ),
    ul: ({ children }) => (
      <Text
        elementType="ul"
        className={cx(
          removeMarkers ? 'list-none' : 'list-inside list-disc',
          removeMarkers ? 'pl-0' : 'pl-8',
          spacingClasses.elements,
          containerClassName,
        )}
      >
        {children}
      </Text>
    ),
    ol: ({ children }) => (
      <Text
        elementType="ol"
        className={cx(
          removeMarkers ? 'list-none' : 'list-inside list-decimal',
          removeMarkers ? 'pl-0' : 'pl-8',
          spacingClasses.elements,
          containerClassName,
        )}
      >
        {children}
      </Text>
    ),
    hr: () => <Separator orientation="horizontal" className={spacingClasses.elements} />,
    code: ({ children, className: codeClassName }) => (
      <Text elementType="code" className={cx('bg-tertiary rounded px-3 py-1 font-mono text-sm', codeClassName)}>
        {children}
      </Text>
    ),
    pre: ({ children }) => (
      <Text
        elementType="pre"
        className={cx('bg-tertiary p-8 overflow-x-auto font-mono text-sm', spacingClasses.elements)}
      >
        {children}
      </Text>
    ),
    blockquote: ({ children }) => (
      <Text elementType="blockquote" className={cx('border-l-4 border-primary pl-8 italic', spacingClasses.elements)}>
        {children}
      </Text>
    ),
    a: ({ children, href }) => (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    ),
    ...componentsOverrides,
  };

  return (
    <div
      data-flow-id={dataFlowId}
      className={cx(spacingClasses.container, dividers && DIVIDER_CLASSES, className)}
      {...other}
    >
      <Markdown components={components} {...options}>
        {children}
      </Markdown>
    </div>
  );
}

export { IdeasMarkdown };
export type { IdeasMarkdownProps, IdeasMarkdownTextOptions, IdeasMarkdownHeadingOptions, IdeasMarkdownListOptions };
